import type { BillingAddon, BillingAddonState } from "@/types/billing.types";

/**
 * Add-on arithmetic, kept out of the components that render it.
 *
 * The picker on `/pricing` and the card on the billing tab show the same three
 * numbers — what you're adding, what it costs, what your limit becomes — from
 * two different shapes of data. Deriving them in one place is what lets a Jest
 * test cover the money without rendering anything.
 */

/** `{ addon_id: quantity }`, the shape both the picker and the API speak. */
export type AddonQuantities = Record<string, number>;

/**
 * Clamp a requested quantity into what the catalogue will actually sell.
 *
 * Not defensive padding: a number input hands back `NaN` for an empty field and
 * for "e", and a `NaN` reaching the total renders the price as "$NaN".
 */
export function clampQuantity(value: unknown, addon: Pick<BillingAddon, "max_quantity">): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, addon.max_quantity);
}

/** Monthly cost of one line, in dollars. */
export function addonLineTotal(addon: Pick<BillingAddon, "amount">, quantity: number): number {
  return round2(addon.amount * Math.max(0, quantity));
}

/** Monthly cost of a whole selection, in dollars. */
export function addonsMonthlyTotal(addons: BillingAddon[], quantities: AddonQuantities): number {
  return round2(
    addons.reduce((sum, addon) => sum + addonLineTotal(addon, quantities[addon.id] ?? 0), 0)
  );
}

/**
 * What changing an add-on costs *per month*, relative to what's already bought.
 *
 * Signed: negative when they're reducing. The confirmation copy needs to know
 * which direction it's going, and the sign is the cheapest way to carry that.
 * This is deliberately NOT the prorated amount charged today — Stripe computes
 * that from the days remaining, and guessing at it in the browser would put a
 * number on screen that the invoice then contradicts.
 */
export function addonMonthlyDelta(addon: BillingAddonState, nextQuantity: number): number {
  return round2(addonLineTotal(addon, nextQuantity) - addonLineTotal(addon, addon.quantity));
}

/**
 * The limit a given add-on quantity produces.
 *
 * `null` in means an uncapped plan, and stays `null` out — an unlimited plan
 * plus three extra deals is still unlimited, not `null + 3`.
 */
export function limitWithAddon(
  base: number | null | undefined,
  addon: Pick<BillingAddon, "grants">,
  limitKey: string,
  quantity: number,
): number | null {
  if (base == null) return null;
  return base + (addon.grants?.[limitKey] ?? 0) * Math.max(0, quantity);
}

/** The add-on in a catalogue that raises a given limit, if any. */
export function addonForLimit<T extends Pick<BillingAddon, "grants">>(
  addons: T[] | undefined,
  limitKey: string,
): T | undefined {
  return (addons ?? []).find(addon => (addon.grants?.[limitKey] ?? 0) > 0);
}

/**
 * The add-ons the public website may show: everything except deal packs.
 *
 * The site deliberately does not say how many deals a plan includes, and a
 * "5 more active deals" line on it would. Deal packs are still sold — in-app,
 * to the plans that have a cap (`BillingAddon.plans`).
 */
export function publicAddons<T extends Pick<BillingAddon, "grants">>(addons: T[] | undefined): T[] {
  return (addons ?? []).filter(addon => (addon.grants?.max_active_deals ?? 0) <= 0);
}

/** Only what actually changed, so an unchanged row isn't posted as a purchase. */
export function changedQuantities(
  addons: BillingAddonState[],
  draft: AddonQuantities,
): { addon: BillingAddonState; quantity: number }[] {
  return addons
    .filter(addon => (draft[addon.id] ?? addon.quantity) !== addon.quantity)
    .map(addon => ({ addon, quantity: draft[addon.id] ?? addon.quantity }));
}

/** Dollar amounts carry cents; floating-point sums of them do not, unaided. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
